import { Parser } from "json2csv";
import moment from "moment";
import AddMemberModal from "./components/AddMemberModal";
import GymMembers from "./GymMembers";

/**
 * Compute monthly active member counts for the chart.
 * Returns the last 6 months of data.
 */
function computeChartData(performClients) {
  const monthsArray = [];
  const totalMembersArray = [];

  for (let i = 5; i >= 0; i--) {
    const month = moment().subtract(i, "months");
    const monthStart = month.clone().startOf("month");
    const monthEnd = month.clone().endOf("month");
    const label = month.format("MMM");

    const activeInMonth = performClients.filter((client) => {
      if (client.status !== "active" && client.status !== "cancelled") {
        return false;
      }
      const approvedAt = client.approvedAt ? moment(client.approvedAt) : null;
      const removedAt = client.removedAt ? moment(client.removedAt) : null;

      if (!approvedAt) return false;
      if (approvedAt.isAfter(monthEnd)) return false;
      if (removedAt?.isBefore(monthStart)) return false;

      return true;
    });

    monthsArray.push(label);
    totalMembersArray.push(activeInMonth.length);
  }

  return { monthsArray, totalMembersArray };
}

export const getRoot = async ({ res, req, locals, load }) => {
  const { modal = {} } = req.query;

  // Active members for the table, enriched with avatars from the API
  const { members: activeMembers } = await load("performMembersList");

  // Chart data
  const chartData = computeChartData(activeMembers);

  // Previous month count for comparison
  const previousMonthCount =
    chartData.totalMembersArray.length >= 2
      ? chartData.totalMembersArray.at(-2)
      : 0;

  // CSV export
  const fields = ["name", "email", "externalId", "_id"];
  const json2csvParser = new Parser({ fields });
  const csv = json2csvParser.parse(
    activeMembers.map((m) => ({
      name: m.name || "",
      email: m.email || "",
      externalId: m.externalId || "",
      _id: m._id?.toString() || "",
    }))
  );

  const statsData = {
    totalMembers: activeMembers.length,
    previousMonthCount,
    chartData,
  };

  locals.modal = {
    "add-member": <AddMemberModal />,
  }[modal.type];

  res.body = (
    <GymMembers
      activeMembers={activeMembers}
      statsData={statsData}
      csvString={csv}
    />
  );
};

export const postAddMember = async ({ res, req, flash, api }) => {
  const { email, name } = req.body;

  try {
    const result = await (
      await api("perform-add-member", {
        method: "POST",
        body: { email, name },
      })
    ).json();

    if (result.success) {
      flash("success", "Member added successfully");
    } else {
      throw new Error(result.error || "Failed to add member");
    }
  } catch (e) {
    console.error("Error adding member:", e);
    flash("error", e.message || "Failed to add member");
    res.backDontScroll();
    return;
  }

  res.refreshWith({}, { url: "/coaching-portal/gym-members", hash: "#_" });
};

export const postRemove = async ({ res, req, flash, api }) => {
  const { clientId } = req.body;

  try {
    const result = await (
      await api("perform-remove-client", {
        method: "POST",
        body: { clientId },
      })
    ).json();

    if (result.success) {
      flash("success", "Member removed successfully");
    } else {
      throw new Error(result.error || "Failed to remove member");
    }
  } catch (e) {
    console.error("Error removing member:", e);
    flash("error", e.message || "Failed to remove member");
  }

  res.refreshWith({}, { url: "/coaching-portal/gym-members", hash: "#_" });
};

export const postConvertToPull = async ({ res, req, flash, api }) => {
  const { clientId } = req.body;

  try {
    const result = await (
      await api("perform-convert-to-pull", {
        method: "POST",
        body: { clientId },
      })
    ).json();

    if (result.success) {
      flash("success", "Member converted to PULL (1:1) client");
    } else {
      throw new Error(result.error || "Failed to convert member");
    }
  } catch (e) {
    console.error("Error converting member to PULL:", e);
    flash("error", e.message || "Failed to convert member");
  }

  res.refreshWith({}, { url: "/coaching-portal/gym-members", hash: "#_" });
};
